import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Checkbox,
  DialogContentText,
  CircularProgress,
  MenuItem
} from '@mui/material';
import { toast } from 'react-toastify';

import segmentService from "../../api/tenantApi";
import { useSegment } from '../../context/segmentContext';
// import { updateCompanySegment } from '../../utils/Helper';
import companyApi from "../../api/companyApi";


const SegmentManager = ({
  isExpanded,
  tenantId,
}) => {
  const { segments, companies, companiesLoading, refreshData } = useSegment();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCompanySelectionOpen, setIsCompanySelectionOpen] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCountry, setSearchCountry] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [newlyCreatedSegment, setNewlyCreatedSegment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Filter companies based on search queries
  const filteredCompanies = companies.filter(company => {
    // Only filter if search criteria are provided
    const nameMatch = !searchQuery || company.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const countryMatch = !searchCountry || company.country?.toLowerCase().includes(searchCountry.toLowerCase());
    return nameMatch && countryMatch;
  });

  // Get unique countries for dropdown
  const uniqueCountries = [...new Set(companies.map(company => company.country).filter(Boolean))].sort();

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCreateSegment = async () => {
    try {
      setIsLoading(true);
      const result = await segmentService.createSegment({
            tenantId,
            name: formData.name.trim(),
            description: formData.description.trim(),
          });
      
      
      const createdSegment = result?.data;
      refreshData(); // Refresh data after creating segment
      setNewlyCreatedSegment(createdSegment);
      setIsAddDialogOpen(false);
      setFormData({ name: '', description: '' });
      setIsCompanySelectionOpen(true);
      toast.success("Segment created successfully");
    } catch (error) {
      toast.error("Failed to create segment");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditSegment = async () => {
    try {
      setIsLoading(true);
      await segmentService.updateSegment(selectedSegment.id,{
            tenantId,
            name: formData.name.trim(),
            description: formData.description.trim(),
          });
      
      refreshData(); // Refresh data after updating segment
      setIsEditDialogOpen(false);
      setSelectedSegment(null);
      setFormData({ name: '', description: '' });
      toast.success("Segment updated successfully");
    } catch (error) {
      toast.error("Failed to update segment");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSegment = async (segment) => {
    if (window.confirm(`Are you sure you want to delete "${segment.name}"?`)) {
      try {
        setIsLoading(true);
        
        // First, get all companies in this segment
        const companiesInSegment = companies.filter(c => c.segmentID === segment.id);
        
        // Update all companies to remove their segmentID
        const updatePromises = companiesInSegment.map(company =>
         companyApi.updateCompany(company.id,{
                tenantId,
                id: company.id,
                segmentID: null,
              })
        );
        
        // Wait for all company updates to complete
        await Promise.all(updatePromises);
        
        // Then delete the segment
        segmentService.deleteSegment(segment.id);
        
        refreshData(); // Refresh data after deleting segment
        toast.success("Segment deleted successfully");
      } catch (error) {
        console.error('Error deleting segment:', error);
        toast.error("Failed to delete segment");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCompanySelectionChange = (company) => {
    setSelectedCompanies(prev => {
      const isSelected = prev.some(c => c.id === company.id);
      if (isSelected) {
        return prev.filter(c => c.id !== company.id);
      }
      return [...prev, company];
    });
  };

  useEffect(() => {
    // When opening the company selection modal for an existing segment,
    // pre-select the companies that are already in that segment
    if (selectedSegment && isCompanySelectionOpen) {
      const companiesInSegment = companies.filter(c => c.segmentID === selectedSegment.id);
      setSelectedCompanies(companiesInSegment);
    }
  }, [selectedSegment, isCompanySelectionOpen, companies]);

  const handleUpdateCompanies = async () => {
    try {
      setIsLoading(true);
      const segmentToUpdate = selectedSegment || newlyCreatedSegment;
      if (!segmentToUpdate) return;

      // Get companies currently in this segment
      const currentSegmentCompanies = companies.filter(c => c.segmentID === segmentToUpdate.id);
      
      // Companies to add (selected but not currently in segment)
      const companiestoAdd = selectedCompanies.filter(
        selected => !currentSegmentCompanies.some(current => current.id === selected.id)
      );
      
      // Companies to remove (currently in segment but not selected)
      const companiestoRemove = currentSegmentCompanies.filter(
        current => !selectedCompanies.some(selected => selected.id === current.id)
      );
      
      const updatePromises = [
        // Add companies to segment
        ...companiestoAdd.map(company => 
          companyApi.updateCompany(company.id,{
                tenantId,
                segmentID: segmentToUpdate.id,
              })
        ),
        // Remove companies from segment
        ...companiestoRemove.map(company =>
          companyApi.updateCompany(company.id,{
                tenantId,
                segmentID: null,
              })
        )
      ];

      console.log('Executing update promises...');
      const results = await Promise.allSettled(updatePromises);
      console.log("Adding and removing companies to segment results:", results);
      
      console.log('Refreshing data and resetting state...');
      refreshData(); // Refresh data after updating companies
      setIsCompanySelectionOpen(false);
      setSelectedSegment(null);
      setNewlyCreatedSegment(null);
      setSelectedCompanies([]);
      setSearchQuery('');
      setSearchCountry('');
      toast.success("Companies updated successfully");
    } catch (error) {
      console.error('Error updating companies:', error);
      toast.error("Failed to update companies");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipCompanySelection = () => {
    setIsCompanySelectionOpen(false);
    setNewlyCreatedSegment(null);
    setSelectedCompanies([]);
    setSearchQuery('');
    setSearchCountry('');
  };

  // Reset search when closing modal
  const handleCloseCompanySelection = () => {
    setSelectedCompanies([]);
    setSelectedSegment(null);
    setIsCompanySelectionOpen(false);
    setSearchQuery('');
    setSearchCountry('');
  };

  if (!isExpanded) return null;

  return (
    <div style={{
      background: '#FAFAFC',
      padding: '20px',
      borderRadius: '0 0 8px 8px',
      boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 1px 3px rgba(0, 0, 0, 0.08)',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Manage Segments</Typography>
        <Button
          variant="contained"
          onClick={() => setIsAddDialogOpen(true)}
          style={{ background: '#5186EC' }}
        >
          Add New Segment
        </Button>
      </Box>

      {segments.length === 0 ? (
        <Typography sx={{ textAlign: 'center', color: '#777A82', py: 3 }}>
          No segments created yet. Create your first segment to organize your companies.
        </Typography>
      ) : (
        segments.map(segment => (
          <Box
            key={segment.id}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid #E9E9E9',
            }}
          >
            <div>
              <Typography>{segment.name}</Typography>
              {segment.description && (
                <Typography variant="body2" color="textSecondary">
                  {segment.description}
                </Typography>
              )}
            </div>
            <div>
              <Button
                onClick={() => {
                  setSelectedSegment(segment);
                  setSelectedCompanies(companies.filter(c => c.segmentID === segment.id));
                  setIsCompanySelectionOpen(true);
                }}
                sx={{ color: '#5186EC' }}
              >
                Manage Companies
              </Button>
              <Button
                onClick={() => {
                  setSelectedSegment(segment);
                  setFormData({
                    name: segment.name,
                    description: segment.description || ''
                  });
                  setIsEditDialogOpen(true);
                }}
                sx={{ color: '#5186EC' }}
              >
                Edit
              </Button>
              <Button
                onClick={() => handleDeleteSegment(segment)}
                sx={{ color: '#FF4D4D' }}
              >
                Delete
              </Button>
            </div>
          </Box>
        ))
      )}

      {/* Company Selection Dialog */}
      <Dialog
        open={isCompanySelectionOpen}
        onClose={handleCloseCompanySelection}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {newlyCreatedSegment ? 'Add Companies to New Segment' : 'Manage Companies in Segment'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {newlyCreatedSegment 
              ? 'Select companies to add to your newly created segment, or skip this step.'
              : 'Select the companies you want to include in this segment.'}
          </DialogContentText>
          
          {/* Company Count Display */}
          <Box sx={{ mt: 1, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {selectedCompanies.length} of {filteredCompanies.length} companies selected
            </Typography>
          </Box>
          
          {/* Search Fields */}
          <Box sx={{ mt: 2, mb: 2, display: 'flex', gap: 2 }}>
            <TextField
              label="Search by company name"
              variant="outlined"
              size="small"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flex: 1 }}
              disabled={companiesLoading}
            />
            <TextField
              select
              label="Filter by country"
              variant="outlined"
              size="small"
              value={searchCountry}
              onChange={(e) => setSearchCountry(e.target.value)}
              sx={{ width: '200px' }}
              disabled={companiesLoading}
            >
              <MenuItem value="">All Countries</MenuItem>
              {uniqueCountries.map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                if (selectedCompanies.length === filteredCompanies.length) {
                  setSelectedCompanies([]);
                } else {
                  setSelectedCompanies(filteredCompanies);
                }
              }}
              disabled={companiesLoading || filteredCompanies.length === 0}
            >
              {selectedCompanies.length === filteredCompanies.length ? 'Deselect All' : 'Select All'}
            </Button>
          </Box>

          <Box 
            sx={{ 
              mt: 2, 
              maxHeight: '400px', 
              overflowY: 'auto',
              position: 'relative',
              minHeight: '200px'
            }}
          >
            {companiesLoading ? (
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(255, 255, 255, 0.8)'
                }}
              >
                <CircularProgress size={40} />
                <Typography sx={{ ml: 2 }}>Loading companies...</Typography>
              </Box>
            ) : filteredCompanies.length > 0 ? (
              filteredCompanies.map((company) => (
                <Box
                  key={company.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    '&:hover': { bgcolor: '#f5f5f5' },
                  }}
                >
                  <Checkbox
                    checked={selectedCompanies.some(c => c.id === company.id)}
                    onChange={() => handleCompanySelectionChange(company)}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>{company.name}</Typography>
                      {company.segmentID && company.segmentID !== (selectedSegment?.id || newlyCreatedSegment?.id) && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            bgcolor: '#e3f2fd', 
                            color: '#1976d2', 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          {segments.find(s => s.id === company.segmentID)?.name || 'Unknown Segment'}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {company.country}
                    </Typography>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography sx={{ textAlign: 'center', color: '#777A82', py: 2 }}>
                {companies.length === 0 
                  ? 'No companies available to add to this segment.'
                  : 'No companies match your search criteria.'}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {newlyCreatedSegment && (
            <Button onClick={handleSkipCompanySelection}>
              Skip for now
            </Button>
          )}
          <Button onClick={handleCloseCompanySelection}>Cancel</Button>
          <Button 
            onClick={handleUpdateCompanies} 
            variant="contained" 
            color="primary"
            disabled={isLoading || companiesLoading}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Segment Dialog */}
      <Dialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)}>
        <DialogTitle>Add New Segment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Segment Name"
            fullWidth
            value={formData.name}
            onChange={handleInputChange('name')}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={handleInputChange('description')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateSegment} 
            variant="contained" 
            color="primary"
            disabled={!formData.name.trim() || isLoading}
          >
            {isLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Segment Dialog */}
      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)}>
        <DialogTitle>Edit Segment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Segment Name"
            fullWidth
            value={formData.name}
            onChange={handleInputChange('name')}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={handleInputChange('description')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleEditSegment} 
            variant="contained" 
            color="primary"
            disabled={!formData.name.trim() || isLoading}
          >
            {isLoading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SegmentManager; 